import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";

export interface DashboardStats {
  inspectionsNotStarted: number;
  inspectionsActive: number;
  inspectionsCompleted: number;
  inspectionsOverdue: number;
  inspectionsCancelled: number;
  inspectionsUnassigned: number;
  inspectionsUnavailableAssignee: number;
  defectsNotStarted: number;
  defectsActive: number;
  defectsToVerify: number;
  defectsCompleted: number;
  defectsOverdue: number;
  defectsCancelled: number;
  defectsUnassigned: number;
  defectsUnavailableAssignee: number;
  totalInspections: number;
  totalDefects: number;
  totalChecklists: number;
}

export interface DashboardFilters {
  dateFrom?: string;
  dateTo?: string;
  inspectionStatusFilter: string;
  defectStatusFilter: string;
  severityFilter: string;
  assignedToFilter: string;
  teamId?: string | null;
}

export interface ProfileInfo {
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  email: string;
  user_id: string;
}

export interface InspectionWithProfiles {
  id: string;
  status: string;
  due_date: string | null;
  created_at: string;
  completed_at: string | null;
  assigned_to: string | null;
  created_by: string | null;
  checklists: { name: string; version: string | null } | null;
  inspection_positions: { name: string; is_archived?: boolean } | null;
  assigned_profile?: ProfileInfo | null;
  created_profile?: ProfileInfo | null;
}

export interface Defect {
  id: string;
  title: string;
  description: string | null;
  severity: string;
  status: string;
  created_at: string;
  due_date: string | null;
  assigned_to: string | null;
}

export interface TrendDataPoint {
  date: string;
  completed: number;
  created: number;
}

export interface DefectTrendDataPoint {
  date: string;
  resolved: number;
  created: number;
}

export interface PieChartDataPoint {
  name: string;
  value: number;
  color: string;
  route?: string;
  severity?: string;
}

interface DashboardData {
  stats: DashboardStats;
  recentInspections: InspectionWithProfiles[];
  criticalDefects: Defect[];
  inspectionTrend: TrendDataPoint[];
  defectTrend: DefectTrendDataPoint[];
  inspectionsByStatus: PieChartDataPoint[];
  inspectionsSimple: PieChartDataPoint[];
  defectsByStatus: PieChartDataPoint[];
  defectsSimple: PieChartDataPoint[];
  defectsBySeverity: PieChartDataPoint[];
}

const dashboardKeys = {
  all: ["dashboard"] as const,
  withFilters: (filters: DashboardFilters, userId: string, canSeeAll: boolean, language: string) =>
    ["dashboard", filters, userId, canSeeAll, language] as const,
};

interface FetchDashboardParams {
  userId: string;
  canSeeAll: boolean;
  isAdmin: boolean;
  isQualitaetsmanager: boolean;
  isTester: boolean;
  filters: DashboardFilters;
  language: string;
  t: (key: string) => string;
}

const fetchDashboardData = async ({
  userId,
  canSeeAll,
  isAdmin,
  isQualitaetsmanager,
  isTester,
  filters,
  language,
  t,
}: FetchDashboardParams): Promise<DashboardData> => {
  const { data, error } = await supabase.functions.invoke("dashboard-metrics", {
    body: {
      userId,
      canSeeAll,
      isAdmin,
      isQualitaetsmanager,
      isTester,
      filters,
      tOpenAll: t("dashboard.openAll"),
      tCompleted: t("dashboard.completed"),
      tNotStarted: t("dashboard.notStarted"),
      tInProgress: t("dashboard.inProgress"),
      tOverdue: t("dashboard.overdue"),
      tUnassigned: t("dashboard.unassigned"),
      tCancelled: t("dashboard.cancelled"),
      tToVerify: t("dashboard.toVerify"),
      tSeverityCritical: t("defects.severity.critical"),
      tSeverityHigh: t("defects.severity.high"),
      tSeverityMedium: t("defects.severity.medium"),
      tSeverityLow: t("defects.severity.low"),
    },
  });

  if (error) {
    throw new Error(error.message || "Failed to fetch dashboard metrics");
  }
  if (!data || data.error) {
    throw new Error(data?.error || "Failed to fetch dashboard metrics");
  }

  return data as DashboardData;
};

export const useDashboardQuery = (
  userId: string | undefined,
  canSeeAll: boolean,
  filters: DashboardFilters,
  options: {
    isAdmin: boolean;
    isQualitaetsmanager: boolean;
    isTester: boolean;
    language: string;
    t: (key: string) => string;
    enabled?: boolean;
  },
) => {
  const queryClient = useQueryClient();

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: dashboardKeys.withFilters(filters, userId || "", canSeeAll, options.language),
    queryFn: () =>
      fetchDashboardData({
        userId: userId!,
        canSeeAll,
        isAdmin: options.isAdmin,
        isQualitaetsmanager: options.isQualitaetsmanager,
        isTester: options.isTester,
        filters,
        language: options.language,
        t: options.t,
      }),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000, // 5 minutes - show cached data instantly, refetch in background
    enabled: !!userId && (options.enabled ?? true),
  });

  // Real-time subscription for cache invalidation (centralized push)
  useRealtimeSubscription(["inspections", "defects"], () => {
    queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
  });

  return {
    stats: data?.stats ?? null,
    recentInspections: data?.recentInspections ?? [],
    criticalDefects: data?.criticalDefects ?? [],
    inspectionTrend: data?.inspectionTrend ?? [],
    defectTrend: data?.defectTrend ?? [],
    inspectionsByStatus: data?.inspectionsByStatus ?? [],
    inspectionsSimple: data?.inspectionsSimple ?? [],
    defectsByStatus: data?.defectsByStatus ?? [],
    defectsSimple: data?.defectsSimple ?? [],
    defectsBySeverity: data?.defectsBySeverity ?? [],
    isLoading,
    isFetching,
    refetch,
  };
};
